import { JSX, useState, useEffect } from "react";
import { JiraIntegrationService } from "../../../services/JiraIntegrationService";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import Button from "../../ui/buttons";

// Import integration icons
import jiraIcon from "../../../assets/icons/svg/setting/jira.svg";
import slackIcon from "../../../assets/icons/svg/setting/slack.svg";
import asanaIcon from "../../../assets/icons/svg/setting/asana.svg";
import figmaIcon from "../../../assets/icons/svg/setting/figma.svg";

interface IntegrationItem {
  id: string;
  name: string;
  description: string;
  icon: string;
  status: "available" | "coming_soon" | "connected";
}

// Add animation styles
const toastStyles = `
  @keyframes slide-in {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`;

// Toast Notification Component
const Toast: React.FC<{
  message: string;
  type: "success" | "error";
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div
        className={`rounded-lg shadow-lg p-4 flex items-center gap-3 ${
          type === "success"
            ? "bg-green-50 text-green-800 border border-green-200"
            : "bg-red-50 text-red-800 border border-red-200"
        }`}
      >
        {type === "success" ? (
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        )}
        <span
          className="font-medium"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {message}
        </span>
        <button onClick={onClose} className="ml-2">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
const ConfirmDialog: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h3
          className="text-lg font-semibold text-gray-900 mb-2"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {title}
        </h3>
        <p
          className="text-gray-600 mb-6"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="cancel"
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 border-0"
            onClick={onCancel}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="px-4 py-2 text-sm hover:bg-red-700"
            onClick={onConfirm}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Disconnect
          </Button>
        </div>
      </div>
    </div>
  );
};

export const IntegrationTab = (): JSX.Element => {
  const [connectionStates, setConnectionStates] = useState<{
    [key: string]: boolean;
  }>({
    jira: false,
    slack: false,
    asana: false,
    figma: false,
  });

  const [isProcessing, setIsProcessing] = useState<{
    [key: string]: boolean;
  }>({});

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // Check Jira connection status on mount
  useEffect(() => {
    checkJiraStatus();
  }, []);

  const checkJiraStatus = async () => {
    try {
      const status = await JiraIntegrationService.checkIntegrationStatus();
      setConnectionStates((prev) => ({
        ...prev,
        jira: status.isConnected,
      }));
    } catch (error) {
      console.error("Error checking Jira status:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const integrations: IntegrationItem[] = [
    {
      id: "jira",
      name: "Jira",
      description:
        "Create tickets, set priorities, and share screenshots and screen recordings - right from the feedback received.",
      icon: jiraIcon,
      status: connectionStates.jira ? "connected" : "available",
    },
    // {
    //   id: "figma",
    //   name: "Figma",
    //   description:
    //     "Create tickets, set priorities, and share screenshots and screen recordings - right from the feedback received.",
    //   icon: figmaIcon,
    //   status: 'coming_soon',
    // },
    {
      id: "slack",
      name: "Slack",
      description: "Get updates or tickets directly on slack.",
      icon: slackIcon,
      status: "coming_soon",
    },
    {
      id: "asana",
      name: "Asana",
      description:
        "Create tickets, set priorities, and share screenshots and screen recordings - right from the feedback received.",
      icon: asanaIcon,
      status: "coming_soon",
    },
  ];

  const handleJiraConnect = async () => {
    setIsProcessing((prev) => ({ ...prev, jira: true }));

    try {
      console.log("🔐 Starting Jira OAuth flow...");

      // Start OAuth authentication flow
      const oauthResult = await JiraIntegrationService.connectJira();

      if (!oauthResult.success) {
        throw new Error(oauthResult.error || "OAuth authentication failed");
      }

      console.log(
        "✅ OAuth completed successfully - connection detected while popup was open",
      );

      // Update connection state immediately
      setConnectionStates((prev) => ({ ...prev, jira: true }));

      // Show success notification
      showNotification("Jira connected successfully!", "success");

      console.log("✅ Jira integration complete");

      // Force check status one more time to ensure UI is updated
      setTimeout(() => {
        checkJiraStatus();
      }, 500);
    } catch (error) {
      console.error("❌ Error connecting to Jira:", error);

      showNotification(
        error instanceof Error ? error.message : "Failed to connect to Jira",
        "error",
      );

      // Revert connection state
      setConnectionStates((prev) => ({ ...prev, jira: false }));
    } finally {
      setIsProcessing((prev) => ({ ...prev, jira: false }));
    }
  };

  const handleJiraDisconnect = async () => {
    setShowDisconnectDialog(false);
    setIsProcessing((prev) => ({ ...prev, jira: true }));

    try {
      console.log("🔄 Disconnecting Jira integration...");

      // Call the disconnect API
      await JiraIntegrationService.disconnect();

      // Update connection state
      setConnectionStates((prev) => ({ ...prev, jira: false }));

      showNotification("Jira disconnected successfully!", "success");
    } catch (error) {
      console.error("❌ Error disconnecting Jira:", error);
      showNotification(
        error instanceof Error ? error.message : "Failed to disconnect Jira",
        "error",
      );
    } finally {
      setIsProcessing((prev) => ({ ...prev, jira: false }));
    }
  };

  const showNotification = (message: string, type: "success" | "error") => {
    setToast({ message, type });
  };

  const renderActionButton = (integration: IntegrationItem) => {
    if (integration.status === "coming_soon") {
      return (
        <Button
          variant="outline"
          disabled
          className="px-4 py-2 text-sm border-[#E88E0F] text-[#E88E0F] bg-white opacity-60"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          Coming Soon
        </Button>
      );
    }

    if (integration.id === "jira") {
      if (loadingStatus) {
        return (
          <div className="px-4 py-2">
            <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full"></div>
          </div>
        );
      }

      if (connectionStates.jira) {
        return (
          <div className="flex items-center gap-3">
            {/* <span className="flex items-center gap-2 text-sm font-medium text-green-600">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Connected
            </span>
            <button
              onClick={() => setShowDisconnectDialog(true)}
              disabled={isProcessing.jira}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-red-500 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing.jira ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                  Disconnecting...
                </span>
              ) : (
                'Disconnect'
              )}
            </button> */}
          </div>
        );
      }

      return (
        <Button
          variant="primary"
          disabled={isProcessing.jira}
          className="px-4 py-2 text-sm flex items-center gap-2"
          onClick={handleJiraConnect}
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          {isProcessing.jira ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>Connecting...</span>
            </>
          ) : (
            "Connect"
          )}
        </Button>
      );
    }

    return (
      <Button
        variant="outline"
        disabled
        className="px-4 py-2 text-sm border-gray-300 text-gray-400"
        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
      >
        Connect
      </Button>
    );
  };

  return (
    <div
      className="px-4 sm:px-6 lg:px-4 xl:px-0"
      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
    >
      {/* Inject animation styles */}
      <style>{toastStyles}</style>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDisconnectDialog}
        title="Disconnect Jira"
        message="Are you sure you want to disconnect Jira? This will disable all Jira features."
        onConfirm={handleJiraDisconnect}
        onCancel={() => setShowDisconnectDialog(false)}
      />

      {/* Header */}
      <div className="mb-4 sm:mb-4">
        <h2
          className="flex items-center gap-2 font-semibold text-lg sm:text-xl lg:text-lg xl:text-base 2xl:text-xl 3xl:text-2xl 4xl:text-3xl text-[#2A3547] mb-2"
          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
        >
          Integrate apps
        </h2>
      </div>

      {/* Integration List */}
      <div className="space-y-0">
        {integrations.map((integration, index) => (
          <div key={integration.id}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 sm:py-4 lg:py-4 gap-4 sm:gap-4">
              <div className="flex items-start gap-3 sm:gap-4 py-4 sm:py-4 lg:py-4">
                {/* Integration Icon */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 xl:w-12 xl:h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 flex items-center justify-center flex-shrink-0">
                  <img
                    src={integration.icon}
                    alt={`${integration.name} icon`}
                    className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 4xl:w-12 4xl:h-12"
                  />
                </div>

                {/* Integration Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    <h3
                      className="font-semibold text-sm sm:text-lg lg:text-lg xl:text-xl 2xl:text-xl 3xl:text-xl 4xl:text-2xl text-[#2A3547]"
                      style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    >
                      {integration.name}
                    </h3>
                    {integration.status === "connected" && (
                      <span
                        className="flex items-center gap-1 text-sm sm:text-sm lg:text-sm xl:text-sm 2xl:text-sm 3xl:text-base 4xl:text-lg font-medium text-[#00BA2F]"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        <span className="w-2 h-2 rounded-full bg-[#00BA2F]"></span>
                        Connected
                      </span>
                    )}
                  </div>
                  <p
                    className="text-xs sm:text-sm lg:text-sm xl:text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl text-[#6C707F] leading-relaxed mb-3"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    {integration.description}
                  </p>

                  {/* Action button below description */}
                  <div className="flex items-center gap-3">
                    {renderActionButton(integration)}
                  </div>
                </div>
              </div>
            </div>

            {/* Separator - don't show after last item */}
            {index < integrations.length - 1 && (
              <div className="border-b border-[#E5E5E5]"></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IntegrationTab;
